import { useCallback, useEffect } from 'react';
import { headerPlugin, http } from '@frontend/fetch';
import { useScopedAppFlagStore } from '@frontend/scope';
import { migrationConfigs, shouldLazyLoad } from '../constants';
import { preferenceActions, usePreferenceStore } from '../store/store';
import { migrateLocalStorage } from '../utils/migration';
import { usePreferenceBulkMutation, usePreferencesBootstrap } from './queries';
import { useUserPreferencesSync } from './use-preference-sync';

export const usePrefBootstrap = () => {
  const isDataInitialized = usePreferenceStore((state) => state.isDataInitialized);
  useUserPreferencesSync();
  const isBootstrapped = usePreferenceStore((state) => state.isBootstrapped);
  const bootstrapQuery = usePreferencesBootstrap();
  const bulkMutation = usePreferenceBulkMutation();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  useEffect(() => {
    const unsubscribe = headerPlugin.subscribeToHeaderChanges(
      'authorization-header',
      (_, value) => {
        if (value) {
          preferenceActions.setIsReady(true);
        }
      },
      'authorization',
      { immediate: true },
      () => http.headers
    );

    return unsubscribe;
  }, []);

  const runMigration = useCallback(async () => {
    if (!isDataInitialized) return;

    const currentPrefs = usePreferenceStore.getState().prefs;
    const bootstrapMigrationConfigs = migrationConfigs.filter((config) => !shouldLazyLoad(config.prefId));

    const needsMigration = bootstrapMigrationConfigs.some((config) => {
      const featureFlagValue = !!config.featureFlagName ? getFeatureFlagValue(config.featureFlagName) : undefined;
      config.stopMigration = featureFlagValue === false;
      return !currentPrefs[config.prefId] && localStorage.getItem(config.localKey) && !config.stopMigration;
    });

    if (needsMigration) {
      await migrateLocalStorage(bootstrapMigrationConfigs, bulkMutation);
    }
  }, [isDataInitialized, getFeatureFlagValue]);

  useEffect(() => {
    runMigration();
  }, [runMigration]);

  return {
    isBootstrapped,
    isLoading: bootstrapQuery.isLoading,
    error: bootstrapQuery.error,
  };
};
